import { useEffect, useRef, RefObject } from 'react';

interface UseScrollLockOptions {
  /**
   * Whether the scroll lock is currently active
   * @default true
   */
  enabled?: boolean;
  /**
   * Whether to also lock the body scroll when the element is focused
   * Useful for modals and overlays
   * @default false
   */
  lockBody?: boolean;
}

/**
 * A hook that prevents scroll propagation from a scrollable element to its parent.
 * This solves the common problem where scrolling inside a container (sidebar, modal, dropdown)
 * causes the background content to scroll when reaching the container's scroll boundaries.
 *
 * @param options - Configuration options
 * @returns A ref to attach to the scrollable element
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const scrollRef = useScrollLock();
 *
 *   return (
 *     <div ref={scrollRef} className="overflow-y-auto h-[400px]">
 *       {// Long scrollable content}
 *     </div>
 *   );
 * };
 * ```
 *
 * @example
 * // With body lock for modals
 * const Modal = ({ isOpen }) => {
 *   const scrollRef = useScrollLock({ enabled: isOpen, lockBody: true });
 *
 *   return (
 *     <div ref={scrollRef} className="modal-content overflow-y-auto">
 *       {// Modal content}
 *     </div>
 *   );
 * };
 */
export function useScrollLock<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollLockOptions = {}
): RefObject<T> {
  const { enabled = true, lockBody = false } = options;
  const ref = useRef<T>(null);
  const originalBodyOverflow = useRef<string>('');

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    // Handle wheel events to prevent scroll propagation
    const getScrollableAncestor = (node: Node | null, stopAt: HTMLElement | null) => {
      let el = node as HTMLElement | null;
      while (el && el !== document.documentElement && el !== stopAt) {
        if (el.scrollHeight > el.clientHeight) return el;
        el = el.parentElement;
      }
      return null;
    };

    const handleWheel = (e: WheelEvent) => {
      // If the wheel event originated from a different scrollable element (e.g., a menu list),
      // allow it to handle the scroll normally.
      try {
        const target = e.target as Node | null;
        const scrollableAncestor = getScrollableAncestor(target, element);
        if (scrollableAncestor && scrollableAncestor !== element) {
          // Let the inner scrollable element handle the wheel.
          return;
        }
      } catch (err) {
        // ignore and continue with default behavior
      }

      const { scrollTop, scrollHeight, clientHeight } = element;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

      // Prevent scroll propagation when at boundaries
      if ((isScrollingUp && isAtTop) || (isScrollingDown && isAtBottom)) {
        e.preventDefault();
      }

      // Stop propagation so parent scroll containers won't also scroll
      e.stopPropagation();
    };

    // Handle touch events for mobile devices
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const touchY = e.touches[0].clientY;
      const isScrollingDown = touchY < touchStartY;
      const isScrollingUp = touchY > touchStartY;

      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

      // Prevent scroll propagation when at boundaries
      if ((isScrollingUp && isAtTop) || (isScrollingDown && isAtBottom)) {
        e.preventDefault();
      }
    };

    // Lock body scroll if requested
    if (lockBody) {
      originalBodyOverflow.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    // Add CSS property for modern browsers (this handles most cases)
    element.style.overscrollBehavior = 'contain';

    // Add event listeners for edge cases and older browsers
    element.addEventListener('wheel', handleWheel, { passive: false });
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      element.removeEventListener('wheel', handleWheel);
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.style.overscrollBehavior = '';

      // Restore body scroll
      if (lockBody) {
        document.body.style.overflow = originalBodyOverflow.current;
      }
    };
  }, [enabled, lockBody]);

  return ref;
}

/**
 * A simpler version that just applies CSS-based scroll containment.
 * Works in all modern browsers but may not prevent all edge cases.
 *
 * @param enabled - Whether the scroll containment is active
 * @returns A ref to attach to the scrollable element
 *
 * @example
 * ```tsx
 * const scrollRef = useScrollContain();
 * return <div ref={scrollRef} className="overflow-y-auto">...</div>;
 * ```
 */
export function useScrollContain<T extends HTMLElement = HTMLDivElement>(
  enabled = true
): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    element.style.overscrollBehavior = 'contain';

    return () => {
      element.style.overscrollBehavior = '';
    };
  }, [enabled]);

  return ref;
}

export default useScrollLock;
